import "mapbox-gl/dist/mapbox-gl.css";
import React, { useState, useRef, useEffect } from "react";
import MapGL, { MapRef, Source, Layer, MapMouseEvent, Popup, Marker } from "react-map-gl";
import Box from "@material-ui/core/Box";
import { t } from "i18next";

import { lightMapStye } from "../../map/Constants";
import { EmergencyMonitoringLegend } from "../../legends/EmergencyMonitoringLegend";
import { darkPurple, greenCyan } from "../../util/colors";
import { tableCellRender } from "../../util/tableUtils";
import Pin from "../../map/pin";
//import { ExtraImageryCheckbox } from "./ExtraImageryCheckbox";
import { getDepthNamesPathsColors } from "./floodDepthUtils";
import { ScaleBar } from "../../map/ScaleBar";
import { calcLngLatByEventCentroid } from "../emergencyUtils";
import { mapDataCloudfrontURL } from "../../map/MapConstants";
import { MapStylesMenu } from "../../map/MapStylesMenu";

import { useClientConfig } from "../../../context/clientConfigContext";

type EmergencyEventDetailsMapProps = {
  emergencyEvent: TerraShieldEvent;
  selectedAoiList: TerraShieldEventAoi[];
  selectedPolicyList: EmergencyPolicy[];
};

type TerraShieldAoiWithMapFiles = TerraShieldEventAoi & {
  aoiFilePath: string;
  observationsFilePath: string;
  policiesAoiFilePath: string;
  policiesFloodFilePath: string;
  claimsAoiFilePath: string;
  claimsFloodFilePath: string;
  builtUpFilePath: string;
  depthNames: string[];
  depthColors: string[];
  depthFilePaths: string[];
};

type MapSelectedPolicy = {
  insuredValue: number;
  multilocationID: string;
  policyNumber: string;
};

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// const circleRadius = {
//   base: 1.75,
//   stops: [
//     [9, 1],
// [12, 3],
// [13, 4],
//     [15, 7],
// [18, 8],
//   ],
// };

const circleRadius: any = [
  "interpolate",
  ["exponential", 1.75], // Exponential interpolation with base 1.75
  ["zoom"],
  9,
  1, // At zoom level 9, circle radius is 1
  15,
  7, // At zoom level 15, circle radius is 7
];

export const EmergencyEventDetailsMap: React.FC<EmergencyEventDetailsMapProps> = ({
  emergencyEvent,
  selectedAoiList,
  selectedPolicyList: markers,
}) => {
  const mapRef = useRef<MapRef>(null);

  const [viewState, setViewState] = useState<PartialViewState>({
    zoom: 8,
  });
  const showDamagedBuildings: boolean = emergencyEvent.code === "EMSR720";

  const [mapStyle, setMapStyle] = useState(lightMapStye);
  const [aoiMapData, setAoiMapData] = useState<TerraShieldAoiWithMapFiles[]>([]);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [selectedPolicy, setSelectedPolicy] = useState<MapSelectedPolicy | null>(null);
  const subdirectory = useClientConfig().emergencyGeoJsonSubdirectory;

  useEffect(() => {
    const fetchAoiData = async () => {
      const aoiWithFiles: TerraShieldAoiWithMapFiles[] = [];

      if (selectedAoiList.length > 0) {
        for (const aoi of selectedAoiList) {
          const s3AoiFolder = `AOI${aoi.number.toString().padStart(2, "0")}`;

          const aoiFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/aois.geojson`;
          const observationsFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/observations.geojson`;
          const policiesAoiFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/policies_aoi.geojson`;
          const policiesFloodFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/policies_flood.geojson`;
          const claimsAoiFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/claims_aoi.geojson`;
          const claimsFloodFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/claims_flood.geojson`;
          const builtUpFilePath = `${subdirectory}/${aoi.activationCode}/${s3AoiFolder}/buildup.geojson`;

          const standardPaths = {
            aoiFilePath: mapDataCloudfrontURL + aoiFilePath,
            observationsFilePath: mapDataCloudfrontURL + observationsFilePath,
            policiesAoiFilePath: mapDataCloudfrontURL + policiesAoiFilePath,
            policiesFloodFilePath: mapDataCloudfrontURL + policiesFloodFilePath,
            claimsAoiFilePath: mapDataCloudfrontURL + claimsAoiFilePath,
            claimsFloodFilePath: mapDataCloudfrontURL + claimsFloodFilePath,
            builtUpFilePath: mapDataCloudfrontURL + builtUpFilePath,
          };

          const depthNamesPathsColors = await getDepthNamesPathsColors(
            mapDataCloudfrontURL,
            subdirectory,
            aoi.activationCode,
            s3AoiFolder,
          );

          aoiWithFiles.push({
            ...aoi,
            ...standardPaths,
            ...depthNamesPathsColors,
          });
        }
      }
      setAoiMapData(aoiWithFiles);
    };

    fetchAoiData();
  }, [selectedAoiList]);

  useEffect(() => {
    if (Object.keys(emergencyEvent).length !== 0) {
      const { lngLat, zoom } = calcLngLatByEventCentroid(emergencyEvent.extent);

      setViewState((oldViewState) => ({
        ...oldViewState,
        longitude: lngLat.lng,
        latitude: lngLat.lat,
        zoom,
      }));
    }
  }, [emergencyEvent]);

  const onMove = (viewState: PartialViewState) => {
    setViewState(viewState);
  };

  const onMapClick = (event: MapMouseEvent) => {
    setLongitude(event.lngLat.lng);
    setLatitude(event.lngLat.lat);
    if (mapRef && mapRef.current) {
      const clickedFeatures = mapRef.current.queryRenderedFeatures(event.point);

      const selectedFeature = clickedFeatures && clickedFeatures[0];
      if (selectedFeature) {
        const { insuredValue, multilocationID, policyNumber } = selectedFeature.properties as any;
        if (insuredValue !== undefined && multilocationID && policyNumber) {
          setSelectedPolicy({ insuredValue, multilocationID, policyNumber });
        } else {
          setSelectedPolicy(null);
        }
      }
    }
  };

  const onPinClick = (markerPolicy: EmergencyPolicy) => {
    setLongitude(markerPolicy.cys_lon);
    setLatitude(markerPolicy.cys_lat);
    setSelectedPolicy({
      insuredValue: markerPolicy.insuredValue,
      multilocationID: markerPolicy.multilocationID,
      policyNumber: markerPolicy.policyNumber,
    });
  };

  return (
    <MapGL
      {...viewState}
      ref={mapRef}
      mapboxAccessToken={TOKEN}
      style={{ width: "100%", height: "100%" }}
      mapStyle={mapStyle}
      onMove={(evt) => onMove(evt.viewState)}
      onClick={onMapClick}
    >
      {/* <ExtraImages activationCode={emergencyEvent.code} /> */}

      {/* show the layers in a certain order to be all polygons and policies visible in case of overlapping */}
      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`aoiLineBox-${index}`}>
            <Source id={`aoiLine-${index}`} type="geojson" data={aoiFiles.aoiFilePath}>
              <Layer
                id={`aoiLayerLine-${index}`}
                type="line"
                source={`aoiLine-${index}`}
                paint={{
                  "line-color": "#ff0000",
                  "line-width": 1,
                }}
              />
            </Source>
          </Box>
        ))}

      {markers &&
        markers.map((marker) => (
          <Marker
            key={`${marker.policyNumber}${marker.multilocationID}`}
            longitude={marker.cys_lon}
            latitude={marker.cys_lat}
          >
            <Pin size={20} onClick={() => onPinClick(marker)} />
          </Marker>
        ))}

      {selectedPolicy && (
        <Popup longitude={longitude} latitude={latitude} onClose={() => setSelectedPolicy(null)}>
          <Box m={1}>
            {/* add policy number secondary for onClick */}
            <div>
              <b>{t("agriculture.policyNo")}</b>: {selectedPolicy.policyNumber}
            </div>
            <div>
              <b>{t("idLocation")}</b>: {selectedPolicy.multilocationID}
            </div>
            <div>
              <b>{t("climateAnalytics.insuredValue")}</b>: €{tableCellRender(selectedPolicy.insuredValue)}
            </div>
          </Box>
        </Popup>
      )}

      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`aoiFillBox-${index}`}>
            <Source id={`aoiFill-${index}`} type="geojson" data={aoiFiles.observationsFilePath}>
              <Layer
                id={`aoiLayerFill-${index}`}
                type="fill"
                source={`aoiFill-${index}`}
                paint={{
                  "fill-color": "#48c6ff",
                }}
                beforeId="sep-layer"
              />
            </Source>
          </Box>
        ))}

      <Layer
        id="sep-layer"
        type="background"
        paint={{ "background-color": "rgba(0,0,0,0)" }}
        // Transparent background needed as a separator, to ensure they layers are displayed before it.
      />

      {aoiMapData.map((aoiFiles, index) =>
        aoiFiles.depthFilePaths.map((depthFilePath, depthIndex) => (
          <Box key={`depthBox-${index}-${depthIndex}`}>
            <Source id={`depth-${index}-${depthIndex}`} type="geojson" data={depthFilePath}>
              <Layer
                id={`depthLayer-${index}-${depthIndex}`}
                type="fill"
                source={`depth-${index}-${depthIndex}`}
                paint={{
                  "fill-color": aoiFiles.depthColors[depthIndex],
                }}
              />
            </Source>
          </Box>
        )),
      )}

      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`policiesAoiBox-${index}`}>
            <Source id={`policiesAoi-${index}`} type="geojson" data={aoiFiles.policiesAoiFilePath}>
              <Layer
                id={`policiesAoiLayer-${index}`}
                type="circle"
                source={`policiesAoi-${index}`}
                paint={{
                  "circle-radius": circleRadius,
                  "circle-color": greenCyan,
                }}
              />
            </Source>
          </Box>
        ))}

      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`policiesFloodAoiBox-${index}`}>
            <Source id={`policiesFloodAoi-${index}`} type="geojson" data={aoiFiles.policiesFloodFilePath}>
              <Layer
                id={`policiesFloodLayer-${index}`}
                type="circle"
                source={`policiesFloodAoi-${index}`}
                paint={{
                  "circle-radius": circleRadius,
                  "circle-color": darkPurple,
                }}
              />
            </Source>
          </Box>
        ))}

      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`claimsAoiBox-${index}`}>
            <Source id={`claimsAoi-${index}`} type="geojson" data={aoiFiles.claimsAoiFilePath}>
              <Layer
                id={`claimsAoiLayer-${index}`}
                type="circle"
                source={`claimsAoi-${index}`}
                paint={{
                  "circle-radius": circleRadius,
                  "circle-color": "orange",
                }}
              />
            </Source>
          </Box>
        ))}

      {aoiMapData.length > 0 &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`claimsFloodBox-${index}`}>
            <Source id={`claimsFlood-${index}`} type="geojson" data={aoiFiles.claimsFloodFilePath}>
              <Layer
                id={`claimsFloodLayer-${index}`}
                type="circle"
                source={`claimsFlood-${index}`}
                paint={{
                  "circle-radius": circleRadius,
                  "circle-color": "red",
                }}
              />
            </Source>
          </Box>
        ))}

      {aoiMapData.length > 0 &&
        showDamagedBuildings &&
        aoiMapData.map((aoiFiles, index) => (
          <Box key={`builtUpBox-${index}`}>
            <Source id={`builtUp-${index}`} type="geojson" data={aoiFiles.builtUpFilePath}>
              <Layer
                id={`builtUpLayer-${index}`}
                type="circle"
                source={`builtUp-${index}`}
                paint={{
                  "circle-radius": circleRadius,
                  "circle-color": [
                    "match",
                    ["get", "damage_gra"],
                    "Possibly damaged",
                    "yellow",
                    "Damaged",
                    "orange",
                    "Destroyed",
                    "red",
                    "green", // default
                  ],
                }}
              />
            </Source>
          </Box>
        ))}

      <EmergencyMonitoringLegend showDamagedBuildings={showDamagedBuildings} />

      <MapStylesMenu setMapStyle={setMapStyle}></MapStylesMenu>

      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};
